/**
 * useWizardDraft Hook
 * 
 * A React hook that integrates with the wizardDraftStore to automatically
 * save and restore wizard form data across tab switches.
 * 
 * Usage:
 * ```tsx
 * const { 
 *   draftData, 
 *   saveDraftData, 
 *   clearDraft, 
 *   hasDraftData 
 * } = useWizardDraft<TemplateDraftData>({
 *   draftType: 'template',
 *   gameId: selectedGame?.id,
 *   itemId: itemId,
 *   enabled: true,
 * });
 * ```
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getDraftKey,
  getDraft,
  saveDraft,
  removeDraft,
  WizardDraftType,
  WizardDraft,
  TemplateDraftData,
  ObjectDraftData,
  DocumentDraftData,
  DialogDraftData,
} from '@/lib/stores/wizardDraftStore';

export type { TemplateDraftData, ObjectDraftData, DocumentDraftData, DialogDraftData };

interface UseWizardDraftOptions<T> {
  draftType: WizardDraftType;
  gameId?: string;
  itemId?: string;
  enabled?: boolean;
  // Auto-save interval in ms (default: 2000ms). Set to 0 to disable.
  autoSaveInterval?: number;
  // Debounce delay for manual saves (default: 500ms)
  saveDebounceMs?: number;
}

interface UseWizardDraftResult<T> {
  /** The loaded draft data, if any exists */
  draftData: T | null;
  /** Whether draft data was found and loaded */
  hasDraftData: boolean;
  /** Save draft data (debounced) */
  saveDraftData: (data: T) => void;
  /** Clear the draft (call after successful save) */
  clearDraft: () => void;
  /** The draft key being used */
  draftKey: string;
  /** Force an immediate save (bypasses debounce) */
  saveImmediately: (data: T) => void;
}

export function useWizardDraft<T extends TemplateDraftData | ObjectDraftData | DocumentDraftData | DialogDraftData>(
  options: UseWizardDraftOptions<T>
): UseWizardDraftResult<T> {
  const {
    draftType,
    gameId,
    itemId,
    enabled = true,
    autoSaveInterval = 0, // Disabled by default - we'll save on unmount
    saveDebounceMs = 500,
  } = options;

  const location = useLocation();
  const draftKey = getDraftKey(location.pathname, itemId);
  
  // Load draft SYNCHRONOUSLY during initial render (not in useEffect)
  // This ensures draftData is available before useBlueprintState initializes
  const [draftData, setDraftData] = useState<T | null>(() => {
    if (!enabled || !gameId) return null;
    const existingDraft = getDraft(draftKey);
    if (existingDraft && existingDraft.gameId === gameId) {
      return existingDraft.data as T;
    }
    return null;
  });
  const [hasDraftData, setHasDraftData] = useState<boolean>(() => {
    if (!enabled || !gameId) return false;
    const existingDraft = getDraft(draftKey);
    return !!(existingDraft && existingDraft.gameId === gameId);
  });
  
  // Refs for tracking pending data and debounce
  const pendingDataRef = useRef<T | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = useRef(true); // Already loaded synchronously

  // When draftKey changes (switching items), reload the draft synchronously
  useEffect(() => {
    if (!enabled || !gameId) {
      setDraftData(null);
      setHasDraftData(false);
      return;
    }
    const existingDraft = getDraft(draftKey);
    if (existingDraft && existingDraft.gameId === gameId) {
      setDraftData(existingDraft.data as T);
      setHasDraftData(true);
    } else {
      setDraftData(null);
      setHasDraftData(false);
    }
  }, [draftKey, enabled, gameId]);

  // Save function (internal)
  const performSave = useCallback((data: T) => {
    if (!enabled || !gameId) return;
    
    saveDraft(draftKey, draftType, gameId, data, itemId);
  }, [enabled, gameId, draftKey, draftType, itemId]);

  // Debounced save
  const saveDraftData = useCallback((data: T) => {
    if (!enabled || !gameId) return;
    
    pendingDataRef.current = data;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      if (pendingDataRef.current) {
        performSave(pendingDataRef.current);
      }
    }, saveDebounceMs);
  }, [enabled, gameId, saveDebounceMs, performSave]);

  // Immediate save (bypasses debounce)
  const saveImmediately = useCallback((data: T) => {
    if (!enabled || !gameId) return;
    
    // Clear pending debounce
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    pendingDataRef.current = data;
    performSave(data);
  }, [enabled, gameId, performSave]);

  // Clear draft
  const clearDraft = useCallback(() => {
    
    // Clear pending saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingDataRef.current = null;
    
    // Remove from store
    removeDraft(draftKey);
    
    // Update local state
    setDraftData(null);
    setHasDraftData(false);
  }, [draftKey, enabled, gameId]);

  // Auto-save interval (if enabled)
  useEffect(() => {
    if (!enabled || !gameId || autoSaveInterval <= 0) return;

    autoSaveIntervalRef.current = setInterval(() => {
      if (pendingDataRef.current) {
        performSave(pendingDataRef.current);
      }
    }, autoSaveInterval);

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [enabled, gameId, autoSaveInterval, performSave]);

  // Save on unmount (component unmounting means tab switch)
  useEffect(() => {
    return () => {
      // Clear debounce timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Save any pending data immediately on unmount
      if (pendingDataRef.current && enabled && gameId) {
        saveDraft(draftKey, draftType, gameId, pendingDataRef.current, itemId);
      }
    };
  }, [enabled, gameId, draftKey, draftType, itemId]);

  return {
    draftData,
    hasDraftData,
    saveDraftData,
    clearDraft,
    draftKey,
    saveImmediately,
  };
}

/**
 * Helper hook to track if draft data should be used for initialization
 * Returns true only once, on the first render where draft data is available
 */
export function useDraftInitialization<T>(
  draftData: T | null,
  hasDraftData: boolean
): { shouldUseDraft: boolean; markDraftUsed: () => void } {
  const hasUsedDraftRef = useRef(false);
  const [shouldUseDraft, setShouldUseDraft] = useState(false);

  useEffect(() => {
    if (hasDraftData && draftData && !hasUsedDraftRef.current) {
      setShouldUseDraft(true);
    }
  }, [hasDraftData, draftData]);

  const markDraftUsed = useCallback(() => {
    hasUsedDraftRef.current = true;
    setShouldUseDraft(false);
  }, []);

  return { shouldUseDraft, markDraftUsed };
}
